// Routes, and the one decision that matters: signed in or not.
//
// There is no route guard component. The whole dashboard lives under <Layout>,
// which is only mounted when there is a user, so an unauthenticated browser
// cannot render a page that would immediately fire an API call and get a 401.
// Terms and Privacy sit outside that split because they have to be readable by
// someone who has not signed up yet.
//
// For a signed-out visitor "/" is the marketing page, not the sign-in form.
// That distinction is the whole reason the site has a front door: asking a
// stranger to create an account before telling them what the product does is
// how you lose them on the first paint. /start goes one step further and lets
// them build the thing first.

import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PageViews } from "./components/PageViews";
import { ScrollToTop } from "./components/ScrollToTop";
import { hasSignedInBefore, useAuth } from "./context/AuthContext";
import { useCan } from "./context/useCan";
import Layout from "./components/Layout";
import StartupFailure from "./components/StartupFailure";
import { LoadingPage } from "./components/ui";
import { hasDraftInProgress } from "./lib/draft";
import { peekReturnPath, rememberReturnPath } from "./lib/returnTo";
import ConfirmEmail from "./pages/ConfirmEmail";
import AccountConfirm from "./pages/AccountConfirm";
import NewAgent from "./pages/NewAgent";
import Home from "./pages/Home";
import NotFound, { NotFoundInApp } from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Compare from "./pages/Compare";
import PageContextGuide from "./pages/PageContextGuide";
import SignIn from "./pages/SignIn";
import Start from "./pages/Start";
import Terms from "./pages/Terms";
import SiteAssistant, { showSiteAssistant } from "./components/SiteAssistant";
import { CUSTOM_WORK_VISIBLE } from "./lib/devTime";

// The signed-in pages load when first opened, so a visitor to the public
// pages does not download the whole dashboard. The wizard stays in the main
// bundle: /start renders it for visitors too.
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const AgentDetail = lazy(() => import("./pages/AgentDetail"));
const Agents = lazy(() => import("./pages/Agents"));
const Billing = lazy(() => import("./pages/Billing"));
const CustomWork = lazy(() => import("./pages/CustomWork"));
const Team = lazy(() => import("./pages/Team"));
const Admin = lazy(() => import("./pages/Admin"));

export default function App() {
  const { user, startupError } = useAuth();
  const { pathname } = useLocation();

  return (
    <>
      {/* Our own help bot: the public pages for visitors deciding, the wizard
          and dashboard for customers building, but not a bot's details page,
          where it covered the Chats reply box. See SiteAssistant. */}
      {showSiteAssistant(pathname) && <SiteAssistant />}
      <ScrollToTop />
      <PageViews />
      <Routes>
        {/* The public paths. They are matched ahead of the split below because
            none of them needs to know who the visitor is, and the front door in
            particular must not wait to find out.

            /start is here rather than in <SignedOut /> because it is the one
            route a visitor is meant to *use* without an account, not a page they
            land on by accident. It has to render the same wizard whether or not
            somebody is signed in, so it cannot live on either side of the split. */}
        <Route path="/" element={<FrontDoor />} />
        <Route path="/start" element={<Start />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/help/page-context" element={<PageContextGuide />} />
        {/* From a handoff confirmation email, opened by whoever owns the
            address -- signed in or not, and often with no account at all. */}
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        {/* The link in our "Confirm your email" email: a person confirming
            their own address, signed in or not. */}
        <Route path="/account/confirm" element={<AccountConfirm />} />

        <Route
          path="/*"
          element={
            startupError ? (
              <StartupFailure message={startupError} />
            ) : user === undefined ? (
              <Booting />
            ) : user ? (
              <Dashboard />
            ) : (
              <SignedOut />
            )
          }
        />
      </Routes>
    </>
  );
}

// What a visitor to the bare domain gets.
//
// Signed in, they are in the product. Signed out -- or not yet known, which is
// most of the first second of every visit -- they get the marketing page, with
// one exception: a browser that has signed in here before waits instead, so an
// existing customer opening buildbotsnow.com does not get the sales pitch
// flashed at them on the way to their dashboard.
function FrontDoor() {
  const { user } = useAuth();

  if (user) return <Navigate to={signedInHome()} replace />;
  if (user === undefined && hasSignedInBefore()) return <Booting />;
  return <Home />;
}

// The first paint after a reload, while Firebase reads its persisted session.
// Rendering the sign-in form here instead would flash it at someone who is
// already signed in.
function Booting() {
  return (
    <div className="min-h-screen bg-slate-50">
      <LoadingPage label="Loading…" />
    </div>
  );
}

// The dashboard's own addresses. Signed out, one of these is a link someone
// will follow once signed in; anything else goes nowhere, and says so.
const APP_PATH = /^\/(agents|billing|team|custom-work|integrations|invite|admin)(\/|$)/;

export function isAppPath(pathname: string): boolean {
  return APP_PATH.test(pathname);
}

function SignedOut() {
  const { pathname } = useLocation();
  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      {/* A dashboard deep link lands on sign-in rather than a 404. A link to
          an agent's page (a handoff's "Take over this chat") is remembered and
          followed once they are in; see lib/returnTo. Any other address is
          the 404. */}
      <Route path="*" element={isAppPath(pathname) ? <RememberAndSignIn /> : <NotFound />} />
    </Routes>
  );
}

function Dashboard() {
  return (
    <Suspense fallback={<LoadingPage label="Loading…" />}>
    <Routes>
      {/* Outside the layout: someone joining from an invite may have no
          account of their own yet, and the layout assumes one. */}
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route element={<Layout />}>
        {/* No "/" route here: it is handled by <FrontDoor /> above.

            Someone who signs in on /signin, or follows an old bookmark to it,
            should land in the product rather than on a form telling them to do
            what they have already done. */}
        <Route path="/signin" element={<Navigate to={signedInHome()} replace />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/agents/new" element={<NewAgent />} />
        <Route path="/agents/:agentId" element={<AgentDetail />} />
        <Route path="/billing" element={<BillingIfAllowed />} />
        <Route path="/team" element={<Team />} />
        {/* Our own view of every account; a 404 for everyone else. */}
        <Route path="/admin" element={<Admin />} />
        <Route path="/custom-work" element={CUSTOM_WORK_VISIBLE ? <CustomWork /> : <Navigate to="/agents" replace />} />
        <Route path="/integrations" element={<LegacyCustomWorkRedirect />} />
        <Route path="*" element={<NotFoundInApp />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

// Billing is for owners and reporting users (lib/roles.ts). Anyone else
// following an old link lands on the agents rather than a refusal.
function BillingIfAllowed() {
  const can = useCan();
  return can("billingRead") ? <Billing /> : <Navigate to="/agents" replace />;
}

// Where signing in lands. Usually the agent list, but a visitor who started the
// wizard signed out and then used "Sign in" rather than the button at the end
// goes back to the wizard: their answers are in the tab's draft, and landing on
// the list made it look as if the bot they were building had been thrown away.
function signedInHome(): string {
  return peekReturnPath() ?? (hasDraftInProgress() ? "/agents/new" : "/agents");
}

// Signed out on a dashboard link: sign in first, then go where the link said.
function RememberAndSignIn() {
  const { pathname, search } = useLocation();
  rememberReturnPath(`${pathname}${search}`);
  return <Navigate to="/signin" replace />;
}

// /integrations was the custom-work page until each agent got an Integrations
// tab of its own. Old links, bookmarks and Stripe success URLs still point here,
// and the query string comes along: ?ordered=1 is how that page knew someone had
// just paid.
export function LegacyCustomWorkRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={CUSTOM_WORK_VISIBLE ? `/custom-work${search}${hash}` : "/agents"} replace />;
}
